var AnalyticsTracker = require('./trackers/analyticsTracker.js');
var PiwikTracker = require('./trackers/piwikTracker.js');
var WebTrendsTracker = require('./trackers/webTrendsTracker.js');
var XitiTracker = require('./trackers/xitiTracker.js');
import {
	ParsedStatisticsXml, StatsConfig, AnalyticsConfigNode, PiwikConfigNode, WebTrendsConfigNode, XitiConfigNode,
	Tracker, TrackingType, Tracking, TrackingPage, TrackingEvent, ResourceCredentials
} from './typings';

import { Options } from 'xml2js';
var xml2js = require('xml2js').parseString;

function xml2jsPromise<T>(xmlString: string, parseOptions: {}): Promise < {} & T > {
	return new Promise(function (resolve: (result: T) => void, reject: (err: {}) => void) {
		xml2js(xmlString, parseOptions, function (err: {}, result: T) {
			if (err) {
				reject(err);
			} else {
				resolve(result);
			}
		});
	});
}

class StatsUtils {

	static self: StatsUtils | undefined = undefined;
	config: StatsConfig;
	trackerList: Tracker[];
	queue: Tracking[];
	resourceCredentials: ResourceCredentials | undefined;
	isMobileApp: boolean;

	parseOptions: Options;

	static getInstance() {
		if (StatsUtils.self === undefined) {
			StatsUtils.self = new StatsUtils();
		}

		return StatsUtils.self;
	}

	constructor() {
		this.parseOptions = {
			async: true,
			explicitChildren: true,

			childkey: 'children',
			attrkey: 'attrs',
		};

		this.queue = [];
		this.trackerList = [];
	}

	updateConfig(configString: string, isMobileApp: boolean, resourceCredentials?: ResourceCredentials) {
		this.isMobileApp = isMobileApp;
		this.resourceCredentials = resourceCredentials;
		xml2jsPromise<ParsedStatisticsXml>(configString, this.parseOptions)
			.then(parsedStatisticsXml => {
				if (parsedStatisticsXml.Statistics && parsedStatisticsXml.Statistics.children) {
					this.config = parsedStatisticsXml.Statistics.children;

					this.updateTrackers();

					this.trackQueue();
				}
			}
		);
	}

	trackPage(page: string, url: string, title: string): void {
		if (this.config === undefined) {
			const toQueue: TrackingPage = {
				type: TrackingType.Page,
				arguments: {
					page: page,
					url: url,
					title: title,
				}
			};
			this.queue.push(toQueue);
			return;
		}

		this.trackPageOnAllTrackers(page, url, title);
	}

	trackEvent(category: string, action: string, label: string, value?: number): void {
		if (this.config === undefined) {
			const toQueue: TrackingEvent = {
				type: TrackingType.Event,
				arguments: {
					category: category,
					action: action,
					label: label,
					value: value,
				}
			};
			this.queue.push(toQueue);
			return;
		}

		this.trackEventOnAllTrackers(category, action, label, value);
	}

	private updateTrackers() {
		this.trackerList = [];

		if (this.config.servers && this.config.servers[0]) {
			const serverConfigs = this.config.servers[0].children;

			if (serverConfigs) {
				if (serverConfigs.GoogleAnalytics) {
					this.updateAnalyticsTrackers(serverConfigs.GoogleAnalytics);
				}

				if (serverConfigs.Piwik) {
					this.updatePiwikTrackers(serverConfigs.Piwik);
				}

				// lib doesnt work in a mobile app
				if (serverConfigs.WebTrends && !this.isMobileApp) {
					this.updateWebTrendsTrackers(serverConfigs.WebTrends);
				}

				// lib doesnt work in a mobile app
				if (serverConfigs.Xiti && !this.isMobileApp) {
					this.updateXitiTrackers(serverConfigs.Xiti);
				}
			}
		}
	}

	private updateAnalyticsTrackers(analyticsConfigs: AnalyticsConfigNode[]) {
		for (let i = 0; i < analyticsConfigs.length; i++) {
			const analyticsConfig = analyticsConfigs[i];
			this.trackerList.push(new AnalyticsTracker(analyticsConfig.attrs));
		}
	}

	private updatePiwikTrackers(piwikConfigs: PiwikConfigNode[]) {
		for (let i = 0; i < piwikConfigs.length; i++) {
			const piwikConfig = piwikConfigs[i];
			this.trackerList.push(new PiwikTracker(piwikConfig.attrs));
		}
	}

	private updateWebTrendsTrackers(webTrendsConfigs: WebTrendsConfigNode[]) {
		for (let i = 0; i < webTrendsConfigs.length; i++) {
			const webTrendsConfig = webTrendsConfigs[i];
			this.trackerList.push(new WebTrendsTracker(webTrendsConfig.attrs));
		}
	}

	private updateXitiTrackers(xitiConfigs: XitiConfigNode[]) {
		for (let i = 0; i < xitiConfigs.length; i++) {
			let xitiConfig = xitiConfigs[i];
			if (xitiConfig.attrs.url) {
				xitiConfig.attrs.url = this.addCredentialsIfNeeded(xitiConfig.attrs.url);
			}

			this.trackerList.push(new XitiTracker(xitiConfig.attrs));
		}
	}

	private trackQueue() {
		for (let tracking = this.queue.pop(); tracking !== undefined; tracking = this.queue.pop()) {
			switch (tracking.type) {
				case TrackingType.Page:
					const trackingPage = tracking as TrackingPage;
					const trackingPageArgs = trackingPage.arguments;
					this.trackPageOnAllTrackers(trackingPageArgs.page, trackingPageArgs.url, trackingPageArgs.title);
					break;
				case TrackingType.Event:
					const trackingEvent = tracking as TrackingEvent;
					const trackingEventArgs = trackingEvent.arguments;
					this.trackEventOnAllTrackers(trackingEventArgs.category, trackingEventArgs.action, trackingEventArgs.label, trackingEventArgs.value);
					break;

			}
		}
	}

	private trackPageOnAllTrackers(page: string, url: string, title: string) {
		for (let i = 0; i < this.trackerList.length; i++) {
			this.trackPageIfLoaded(this.trackerList[i], page, url, title, 3);
		}
	}

	private trackEventOnAllTrackers(category: string, action: string, label: string, value?: number) {
		for (let i = 0; i < this.trackerList.length; i++) {
			this.trackEventIfLoaded(this.trackerList[i], category, action, label, value, 3);
		}
	}

	private trackPageIfLoaded(tracker: Tracker, page: string, url: string, title: string, triesLeft: number) {
		if (tracker.isLoaded()) {
			tracker.trackPage(page, url, title);
			return;
		}
		if (triesLeft > 0) {
			setTimeout(
				() => {
					this.trackPageIfLoaded(tracker, page, url, title, triesLeft - 1);
				},
				2000
			);
		}
	}

	private trackEventIfLoaded(tracker: Tracker, category: string, action: string, label: string, value: number|undefined, triesLeft: number) {
		if (tracker.isLoaded()) {
			tracker.trackEvent(category, action, label, value);
			return;
		}
		if (triesLeft > 0) {
			setTimeout(
				() => {
					this.trackEventIfLoaded(tracker, category, action, label, value, triesLeft - 1);
				},
				2000
			);
		}
	}

	private addCredentialsIfNeeded(url: string) {
		if (this.resourceCredentials === undefined) {
			return url;
		}

		const { login, password } = this.resourceCredentials;
		const includesSeparator = url.includes('?');
		const startingSeparator = includesSeparator ? '&' : '?';
		const credentials = `${startingSeparator}user=${login}&pass=${password}`;

		return url + credentials;
	}
}

export default StatsUtils;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/statistics/trackerManager.ts